// src/components/Spreadsheet/groupedSpreadsheetUtils.js

/**
 * groupedSpreadsheetUtils
 * -----------------------------------------------------------------------------
 * Pure utility helpers for the shared grouped spreadsheet engine.
 *
 * PURPOSE
 *  • Keep spreadsheet behavior reusable across Form Lists and SmartLists
 *  • Avoid coupling shared grid behavior to any domain schema
 *  • Support Google-Sheets-like local editing without eager full-table saves
 *  • Provide stable helpers for:
 *      - row identity
 *      - row padding
 *      - blank-row detection
 *      - outgoing snapshot cleanup
 *      - dirty group / row / cell tracking
 *      - keyboard movement
 *      - clipboard grid parsing
 *      - column width normalization
 *
 * IMPORTANT
 *  • This file does NOT import React.
 *  • This file does NOT import Firebase.
 *  • This file does NOT know about:
 *      - fieldsMetadata
 *      - dropdownOptions
 *      - dropdownDependencies
 *      - managedFields
 *      - managedOptionSets
 *  • Domain adapters must provide the right group, row, and column shape.
 * -----------------------------------------------------------------------------
 */

export const DEFAULT_VISIBLE_ROW_COUNT = 50;
export const DEFAULT_ROW_APPEND_COUNT = 25;

export const DEFAULT_ROW_ID_KEY = 'rowId';
export const DEFAULT_GROUP_ID_KEY = 'groupId';

export const SPREADSHEET_SORT_DIRECTIONS = Object.freeze({
  ASCENDING: 'ascending',
  DESCENDING: 'descending',
});

export const INTERNAL_ROW_FLAGS = Object.freeze({
  IS_BLANK: '__isBlankRow',
  IS_PADDED: '__isPaddedRow',
  ROW_INDEX: '__rowIndex',
});

/* -------------------------------------------------------------------------- */
/* Basic guards                                                               */
/* -------------------------------------------------------------------------- */

export const isPlainObject = (value) => (
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

export const toArraySafe = (value) => (
  Array.isArray(value) ? value : []
);

export const normalizeString = (value, fallback = '') => (
  typeof value === 'string' ? value.trim() : fallback
);

export const isNonEmptyString = (value) => (
  normalizeString(value, '').length > 0
);

export const toFiniteNumber = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

export const clampNumber = (value, min, max) => {
  const next = toFiniteNumber(value, min);
  return Math.min(Math.max(next, min), max);
};

/* -------------------------------------------------------------------------- */
/* Column helpers                                                             */
/* -------------------------------------------------------------------------- */

export const getColumnKey = (column) => (
  normalizeString(column?.key || column?.id, '')
);

/**
 * getColumnWidthKey
 * -----------------------------------------------------------------------------
 * Builds the persisted/rendered width key for one visual spreadsheet column.
 *
 * WHY
 *  • In a grouped spreadsheet, the same logical column can appear many times:
 *      Group A Option, Group B Option, Group C Option, ...
 *  • A local spreadsheet / Google Sheets treats those as different visual
 *    columns.
 *  • Width identity must therefore include the groupId when one is available.
 *
 * BACKWARD COMPATIBILITY
 *  • If no groupId is supplied, this returns the plain logical column key.
 *  • Existing consumers that store widths as { [columnKey]: width } continue
 *    to work as defaults/fallbacks.
 */
export const getColumnWidthKey = ({
  groupId,
  column,
  columnKey,
} = {}) => {
  const safeColumnKey = normalizeString(columnKey || getColumnKey(column), '');

  if (!safeColumnKey) {
    return '';
  }

  const safeGroupId = normalizeString(groupId, '');

  return safeGroupId
    ? `${safeGroupId}::column::${safeColumnKey}`
    : safeColumnKey;
};

/**
 * getGroupIdForColumnWidths
 * -----------------------------------------------------------------------------
 * Internal helper used only by column-width normalization.
 *
 * IMPORTANT
 *  • This mirrors getGroupId(...) without moving the group identity section
 *    above the column helpers.
 *  • It keeps this column helper section self-contained and avoids coupling
 *    width normalization to declaration order.
 */
const getGroupIdForColumnWidths = (group = {}) => (
  normalizeString(
    group?.[DEFAULT_GROUP_ID_KEY] ||
      group?.id ||
      group?.localDraftId ||
      group?.optionsSetUFI,
    ''
  )
);

export const getVisibleColumns = (columns = []) => (
  toArraySafe(columns).filter((column) => {
    if (!column) return false;
    if (column.hidden === true) return false;
    return !!getColumnKey(column);
  })
);

export const getEditableColumns = (columns = []) => (
  getVisibleColumns(columns).filter((column) => {
    if (column.readOnly === true) return false;
    if (column.type === 'custom' && column.editable !== true) return false;
    return true;
  })
);

export const getPasteTargetColumns = (columns = []) => (
  getEditableColumns(columns).filter((column) => {
    if (column.excludeFromPaste === true) return false;
    return true;
  })
);

export const buildDefaultColumnWidths = (columns = []) => {
  return getVisibleColumns(columns).reduce((acc, column) => {
    const key = getColumnKey(column);
    const width = toFiniteNumber(column.width, 100);

    acc[key] = width;
    return acc;
  }, {});
};

/**
 * buildDefaultColumnWidthsForGroups
 * -----------------------------------------------------------------------------
 * Builds default widths for every visual grouped column instance.
 *
 * WHY
 *  • Form Lists, SmartLists, and future grouped sheets can reuse the same
 *    logical column definitions across many groups.
 *  • The spreadsheet engine must still render and resize each visible group
 *    column independently.
 *
 * OUTPUT SHAPE
 *  • Includes plain keys for backward-compatible defaults:
 *      { label: 110, hint: 100 }
 *  • Includes scoped visual keys for Google-Sheets-like resizing:
 *      { "groupA::column::label": 110 }
 */
export const buildDefaultColumnWidthsForGroups = ({
  groups = [],
  columns = [],
} = {}) => {
  const next = {
    ...buildDefaultColumnWidths(columns),
  };

  toArraySafe(groups).forEach((group) => {
    const groupId = getGroupIdForColumnWidths(group);

    const groupColumns =
      Array.isArray(group?.columns) && group.columns.length > 0
        ? group.columns
        : columns;

    getVisibleColumns(groupColumns).forEach((column) => {
      const columnKey = getColumnKey(column);
      const widthKey = getColumnWidthKey({
        groupId,
        column,
        columnKey,
      });

      if (!widthKey) return;

      next[widthKey] = toFiniteNumber(column.width, 100);
    });
  });

  return next;
};

export const normalizeColumnWidths = ({
  columns = [],
  groups = [],
  columnWidths = {},
} = {}) => {
  const safeGroups = toArraySafe(groups);

  const defaults =
    safeGroups.length > 0
      ? buildDefaultColumnWidthsForGroups({
          groups: safeGroups,
          columns,
        })
      : buildDefaultColumnWidths(columns);

  const next = { ...defaults };

  const findColumnForWidthKey = (widthKey) => {
    const safeWidthKey = normalizeString(widthKey, '');
    if (!safeWidthKey) return null;

    const scopedColumnKey = safeWidthKey.includes('::column::')
      ? safeWidthKey.split('::column::').pop()
      : safeWidthKey;

    const topLevelMatch = getVisibleColumns(columns).find(
      (candidate) => getColumnKey(candidate) === scopedColumnKey
    );

    if (topLevelMatch) return topLevelMatch;

    for (const group of safeGroups) {
      const groupColumns =
        Array.isArray(group?.columns) && group.columns.length > 0
          ? group.columns
          : columns;

      const groupMatch = getVisibleColumns(groupColumns).find(
        (candidate) => getColumnKey(candidate) === scopedColumnKey
      );

      if (groupMatch) return groupMatch;
    }

    return null;
  };

  Object.entries(columnWidths || {}).forEach(([key, width]) => {
    const column = findColumnForWidthKey(key);

    if (!column) return;

    const minWidth = toFiniteNumber(column.minWidth, 40);
    const maxWidth = toFiniteNumber(column.maxWidth, 720);

    next[key] = clampNumber(width, minWidth, maxWidth);
  });

  return next;
};

export const getColumnWidth = ({
  groupId,
  column,
  columnKey,
  columnWidths = {},
  fallback = 100,
} = {}) => {
  const key = normalizeString(columnKey || getColumnKey(column), '');
  if (!key) return fallback;

  const scopedWidthKey = getColumnWidthKey({
    groupId,
    column,
    columnKey: key,
  });

  const minWidth = toFiniteNumber(column?.minWidth, 40);
  const maxWidth = toFiniteNumber(column?.maxWidth, 720);

  const width =
    columnWidths?.[scopedWidthKey] ??
    columnWidths?.[key] ??
    column?.width ??
    fallback;

  return clampNumber(width, minWidth, maxWidth);
};

export const getColumnIndexByKey = (columns = [], columnKey) => {
  const visibleColumns = getVisibleColumns(columns);
  return visibleColumns.findIndex(
    (column) => getColumnKey(column) === columnKey
  );
};

/* -------------------------------------------------------------------------- */
/* Group and row identity helpers                                             */
/* -------------------------------------------------------------------------- */

export const getGroupId = (
  group,
  groupIdKey = DEFAULT_GROUP_ID_KEY,
) => {
  return normalizeString(
    group?.[groupIdKey] ||
      group?.id ||
      group?.localDraftId ||
      group?.optionsSetUFI,
    ''
  );
};

export const getRowId = (
  row,
  rowIdKey = DEFAULT_ROW_ID_KEY,
) => {
  return normalizeString(row?.[rowIdKey], '');
};

export const buildGeneratedRowId = ({
  groupId,
  rowIndex,
  prefix = 'sheet-row',
} = {}) => {
  const safeGroupId = normalizeString(groupId, 'group');
  const safeIndex = Number.isFinite(Number(rowIndex))
    ? Number(rowIndex)
    : 0;

  return `${prefix}-${safeGroupId}-${safeIndex}`;
};

export const ensureRowIdentity = ({
  row = {},
  groupId,
  rowIndex = 0,
  rowIdKey = DEFAULT_ROW_ID_KEY,
  generatedPrefix = 'sheet-row',
} = {}) => {
  const existingRowId = getRowId(row, rowIdKey);

  if (existingRowId) {
    return row;
  }

  return {
    ...row,
    [rowIdKey]: buildGeneratedRowId({
      groupId,
      rowIndex,
      prefix: generatedPrefix,
    }),
  };
};

export const createBlankRow = ({
  columns = [],
  rowId,
  rowIdKey = DEFAULT_ROW_ID_KEY,
  extra = {},
} = {}) => {
  const row = {
    [rowIdKey]: rowId,
    ...extra,
  };

  getVisibleColumns(columns).forEach((column) => {
    const key = getColumnKey(column);
    if (!key) return;

    if (Object.prototype.hasOwnProperty.call(row, key)) {
      return;
    }

    row[key] = column.defaultValue ?? '';
  });

  return row;
};

/* -------------------------------------------------------------------------- */
/* Row normalization and blank-row detection                                  */
/* -------------------------------------------------------------------------- */

export const normalizeSpreadsheetRow = ({
  row = {},
  columns = [],
  groupId,
  rowIndex = 0,
  rowIdKey = DEFAULT_ROW_ID_KEY,
  generatedPrefix = 'sheet-row',
} = {}) => {
  const rowWithIdentity = ensureRowIdentity({
    row,
    groupId,
    rowIndex,
    rowIdKey,
    generatedPrefix,
  });

  const nextRow = {
    ...rowWithIdentity,
    [INTERNAL_ROW_FLAGS.ROW_INDEX]: rowIndex,
  };

  getVisibleColumns(columns).forEach((column) => {
    const key = getColumnKey(column);
    if (!key) return;

    if (!Object.prototype.hasOwnProperty.call(nextRow, key)) {
      nextRow[key] = column.defaultValue ?? '';
    }
  });

  return nextRow;
};

export const isMeaningfulCellValue = (value) => {
  if (value == null) return false;

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'boolean') {
    return value === true;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return false;
};

export const isMeaningfulSpreadsheetRow = ({
  row = {},
  columns = [],
} = {}) => {
  if (!row || typeof row !== 'object') return false;

  return getVisibleColumns(columns).some((column) => {
    const key = getColumnKey(column);
    if (!key) return false;

    if (column.persist === false) return false;
    if (column.excludeFromMeaningfulCheck === true) return false;

    if (typeof column.isMeaningful === 'function') {
      return column.isMeaningful(row?.[key], row, column) === true;
    }

    return isMeaningfulCellValue(row?.[key]);
  });
};

export const markRowBlankFlags = ({
  row = {},
  columns = [],
  isPadded = false,
} = {}) => {
  const isBlank = !isMeaningfulSpreadsheetRow({ row, columns });

  return {
    ...row,
    [INTERNAL_ROW_FLAGS.IS_BLANK]: isBlank,
    [INTERNAL_ROW_FLAGS.IS_PADDED]: isPadded === true,
  };
};

export const normalizeSpreadsheetRows = ({
  rows = [],
  columns = [],
  groupId,
  rowIdKey = DEFAULT_ROW_ID_KEY,
  generatedPrefix = 'sheet-row',
} = {}) => {
  return toArraySafe(rows).map((row, rowIndex) => {
    const normalized = normalizeSpreadsheetRow({
      row,
      columns,
      groupId,
      rowIndex,
      rowIdKey,
      generatedPrefix,
    });

    return markRowBlankFlags({
      row: normalized,
      columns,
      isPadded: false,
    });
  });
};

export const padRowsToVisibleCount = ({
  rows = [],
  columns = [],
  visibleRowCount = DEFAULT_VISIBLE_ROW_COUNT,
  groupId,
  rowIdKey = DEFAULT_ROW_ID_KEY,
  generatedPrefix = 'sheet-row',
} = {}) => {
  const normalizedRows = normalizeSpreadsheetRows({
    rows,
    columns,
    groupId,
    rowIdKey,
    generatedPrefix,
  });

  const targetCount = Math.max(
    toFiniteNumber(visibleRowCount, DEFAULT_VISIBLE_ROW_COUNT),
    normalizedRows.length,
  );

  const paddedRows = [...normalizedRows];

  for (let rowIndex = paddedRows.length; rowIndex < targetCount; rowIndex += 1) {
    const blankRow = createBlankRow({
      columns,
      rowId: buildGeneratedRowId({
        groupId,
        rowIndex,
        prefix: generatedPrefix,
      }),
      rowIdKey,
    });

    const normalizedBlank = normalizeSpreadsheetRow({
      row: blankRow,
      columns,
      groupId,
      rowIndex,
      rowIdKey,
      generatedPrefix,
    });

    paddedRows.push(
      markRowBlankFlags({
        row: normalizedBlank,
        columns,
        isPadded: true,
      }),
    );
  }

  return paddedRows;
};

export const stripInternalRowFlags = (row = {}) => {
  const {
    [INTERNAL_ROW_FLAGS.IS_BLANK]: _isBlank,
    [INTERNAL_ROW_FLAGS.IS_PADDED]: _isPadded,
    [INTERNAL_ROW_FLAGS.ROW_INDEX]: _rowIndex,
    ...rest
  } = row || {};

  return rest;
};

export const buildOutgoingRows = ({
  rows = [],
  columns = [],
  includeBlankRows = false,
  stripInternalFlags = true,
} = {}) => {
  return toArraySafe(rows)
    .filter((row) => {
      if (includeBlankRows) return true;
      return isMeaningfulSpreadsheetRow({ row, columns });
    })
    .map((row) => (
      stripInternalFlags ? stripInternalRowFlags(row) : row
    ));
};

/* -------------------------------------------------------------------------- */
/* Group helpers                                                              */
/* -------------------------------------------------------------------------- */

export const normalizeSpreadsheetGroup = ({
  group = {},
  columns = [],
  groupIdKey = DEFAULT_GROUP_ID_KEY,
  rowIdKey = DEFAULT_ROW_ID_KEY,
  rowKey = 'rows',
  visibleRowCount = DEFAULT_VISIBLE_ROW_COUNT,
  generatedPrefix = 'sheet-row',
} = {}) => {
  const groupId = getGroupId(group, groupIdKey);

  const sourceRows = Array.isArray(group?.[rowKey])
    ? group[rowKey]
    : [];

  return {
    ...group,
    [groupIdKey]: group?.[groupIdKey] || groupId,
    columns: Array.isArray(group?.columns) ? group.columns : columns,
    [rowKey]: padRowsToVisibleCount({
      rows: sourceRows,
      columns,
      visibleRowCount,
      groupId,
      rowIdKey,
      generatedPrefix,
    }),
  };
};

export const buildGroupRowsSnapshot = ({
  group = {},
  columns = [],
  rowKey = 'rows',
  includeBlankRows = false,
} = {}) => {
  const rows = Array.isArray(group?.[rowKey]) ? group[rowKey] : [];

  return buildOutgoingRows({
    rows,
    columns,
    includeBlankRows,
  });
};

/* -------------------------------------------------------------------------- */
/* Dirty-state helpers                                                        */
/* -------------------------------------------------------------------------- */

export const createEmptyDirtyState = () => ({
  dirtyGroupIds: new Set(),
  dirtyRowsByGroup: {},
  dirtyCellsByGroup: {},
});

const cloneSet = (value) => {
  if (value instanceof Set) return new Set(value);
  if (Array.isArray(value)) return new Set(value);
  return new Set();
};

export const markGroupDirty = (dirtyState, groupId) => {
  const safeGroupId = normalizeString(groupId, '');

  if (!safeGroupId) {
    return dirtyState || createEmptyDirtyState();
  }

  const base = dirtyState || createEmptyDirtyState();
  const dirtyGroupIds = cloneSet(base.dirtyGroupIds);

  dirtyGroupIds.add(safeGroupId);

  return {
    ...base,
    dirtyGroupIds,
  };
};

export const markRowDirty = (dirtyState, groupId, rowId) => {
  const safeGroupId = normalizeString(groupId, '');
  const safeRowId = normalizeString(rowId, '');

  if (!safeGroupId || !safeRowId) {
    return dirtyState || createEmptyDirtyState();
  }

  const next = markGroupDirty(dirtyState, safeGroupId);
  const rowSet = cloneSet(next.dirtyRowsByGroup?.[safeGroupId]);

  rowSet.add(safeRowId);

  return {
    ...next,
    dirtyRowsByGroup: {
      ...(next.dirtyRowsByGroup || {}),
      [safeGroupId]: rowSet,
    },
  };
};

export const markCellDirty = (
  dirtyState,
  groupId,
  rowId,
  columnKey,
) => {
  const safeGroupId = normalizeString(groupId, '');
  const safeRowId = normalizeString(rowId, '');
  const safeColumnKey = normalizeString(columnKey, '');

  if (!safeGroupId || !safeRowId || !safeColumnKey) {
    return dirtyState || createEmptyDirtyState();
  }

  const next = markRowDirty(dirtyState, safeGroupId, safeRowId);
  const cellsByRow = next.dirtyCellsByGroup?.[safeGroupId] || {};
  const cellSet = cloneSet(cellsByRow?.[safeRowId]);

  cellSet.add(safeColumnKey);

  return {
    ...next,
    dirtyCellsByGroup: {
      ...(next.dirtyCellsByGroup || {}),
      [safeGroupId]: {
        ...cellsByRow,
        [safeRowId]: cellSet,
      },
    },
  };
};

export const isGroupDirty = (dirtyState, groupId) => {
  const safeGroupId = normalizeString(groupId, '');
  if (!safeGroupId) return false;

  return dirtyState?.dirtyGroupIds instanceof Set
    ? dirtyState.dirtyGroupIds.has(safeGroupId)
    : false;
};

export const isRowDirty = (dirtyState, groupId, rowId) => {
  const safeGroupId = normalizeString(groupId, '');
  const safeRowId = normalizeString(rowId, '');

  if (!safeGroupId || !safeRowId) return false;

  const rowSet = dirtyState?.dirtyRowsByGroup?.[safeGroupId];
  return rowSet instanceof Set ? rowSet.has(safeRowId) : false;
};

export const getDirtyGroupIdsArray = (dirtyState) => {
  if (!(dirtyState?.dirtyGroupIds instanceof Set)) return [];
  return Array.from(dirtyState.dirtyGroupIds);
};

export const clearDirtyStateForGroup = (dirtyState, groupId) => {
  const safeGroupId = normalizeString(groupId, '');

  if (!safeGroupId || !dirtyState) {
    return dirtyState || createEmptyDirtyState();
  }

  const dirtyGroupIds = cloneSet(dirtyState.dirtyGroupIds);
  dirtyGroupIds.delete(safeGroupId);

  const {
    [safeGroupId]: _rowsForGroup,
    ...dirtyRowsByGroup
  } = dirtyState.dirtyRowsByGroup || {};

  const {
    [safeGroupId]: _cellsForGroup,
    ...dirtyCellsByGroup
  } = dirtyState.dirtyCellsByGroup || {};

  return {
    dirtyGroupIds,
    dirtyRowsByGroup,
    dirtyCellsByGroup,
  };
};

export const clearDirtyState = () => createEmptyDirtyState();

/* -------------------------------------------------------------------------- */
/* Sorting helpers                                                            */
/* -------------------------------------------------------------------------- */

export const normalizeSortDirection = (direction) => {
  const safeDirection = normalizeString(direction, '');

  if (safeDirection === SPREADSHEET_SORT_DIRECTIONS.ASCENDING) {
    return SPREADSHEET_SORT_DIRECTIONS.ASCENDING;
  }

  if (safeDirection === SPREADSHEET_SORT_DIRECTIONS.DESCENDING) {
    return SPREADSHEET_SORT_DIRECTIONS.DESCENDING;
  }

  return null;
};

export const getNextSortDirection = ({
  currentSort = null,
  groupId,
  columnKey,
} = {}) => {
  const safeGroupId = normalizeString(groupId, '');
  const safeColumnKey = normalizeString(columnKey, '');

  if (!safeGroupId || !safeColumnKey) {
    return SPREADSHEET_SORT_DIRECTIONS.ASCENDING;
  }

  const sameColumn =
    normalizeString(currentSort?.groupId, '') === safeGroupId &&
    normalizeString(currentSort?.columnKey, '') === safeColumnKey;

  if (!sameColumn) {
    return SPREADSHEET_SORT_DIRECTIONS.ASCENDING;
  }

  return normalizeSortDirection(currentSort?.direction) ===
    SPREADSHEET_SORT_DIRECTIONS.ASCENDING
    ? SPREADSHEET_SORT_DIRECTIONS.DESCENDING
    : SPREADSHEET_SORT_DIRECTIONS.ASCENDING;
};

export const isSpreadsheetColumnSortable = (column = {}) => {
  if (!column) return false;
  if (column.sortable !== true) return false;
  if (column.type === 'custom') return false;
  return true;
};

const isEmptySortValue = (value) => {
  if (value == null) return true;

  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

const normalizeSortValue = (value) => {
  if (value == null) return '';

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : '';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return String(value).trim();
};

const isNumericSortValue = (value) => {
  const normalized = normalizeSortValue(value);

  if (typeof normalized === 'number') {
    return Number.isFinite(normalized);
  }

  if (typeof normalized !== 'string' || normalized.length === 0) {
    return false;
  }

  return /^[-+]?(?:\d+|\d*\.\d+)(?:e[-+]?\d+)?$/i.test(normalized);
};

const compareSpreadsheetSortValues = ({
  a,
  b,
  direction,
} = {}) => {
  const safeDirection =
    normalizeSortDirection(direction) || SPREADSHEET_SORT_DIRECTIONS.ASCENDING;

  const aEmpty = isEmptySortValue(a);
  const bEmpty = isEmptySortValue(b);

  /**
   * Empty values always sort to the bottom.
   *
   * WHY
   *  • Spreadsheet users expect blank rows/blank cells to stay out of the way.
   *  • This also keeps padded blank rows at the bottom in both directions.
   *  • Descending sort should not pull empty values to the top.
   */
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let result = 0;

  if (isNumericSortValue(a) && isNumericSortValue(b)) {
    result = Number(normalizeSortValue(a)) - Number(normalizeSortValue(b));
  } else {
    result = String(normalizeSortValue(a)).localeCompare(
      String(normalizeSortValue(b)),
      undefined,
      {
        numeric: true,
        sensitivity: 'base',
      },
    );
  }

  return safeDirection === SPREADSHEET_SORT_DIRECTIONS.DESCENDING
    ? result * -1
    : result;
};

const getRowSortValue = ({
  row,
  column,
  columnKey,
} = {}) => {
  if (!row || !columnKey) return '';

  if (typeof column?.getSortValue === 'function') {
    return column.getSortValue(row?.[columnKey], row, column);
  }

  return row?.[columnKey];
};

/**
 * sortSpreadsheetRowsForColumn
 * -----------------------------------------------------------------------------
 * Stable row-sort helper for the shared grouped spreadsheet engine.
 *
 * WHY THIS EXISTS
 *  • Sorting must be a reusable Spreadsheet capability, not a Form Lists-only
 *    page behavior.
 *  • Rows must move as whole objects so rowId, optionId, dependency state,
 *    hints, values, and internal flags stay attached to the same logical row.
 *  • Blank padded rows should remain at the bottom.
 *
 * ENTERPRISE RULES
 *  • This is domain-free.
 *  • This does not know about Form Lists, SmartLists, or Firestore.
 *  • This does not mutate the input rows.
 *  • This does not write context or storage.
 *  • Stable sort preserves existing order when values compare equal.
 */
export const sortSpreadsheetRowsForColumn = ({
  rows = [],
  columns = [],
  columnKey,
  direction = SPREADSHEET_SORT_DIRECTIONS.ASCENDING,
} = {}) => {
  const safeDirection = normalizeSortDirection(direction);

  if (!safeDirection) {
    return toArraySafe(rows);
  }

  const safeColumnKey = normalizeString(columnKey, '');
  if (!safeColumnKey) {
    return toArraySafe(rows);
  }

  const column = getVisibleColumns(columns).find(
    (candidate) => getColumnKey(candidate) === safeColumnKey,
  );

  if (!isSpreadsheetColumnSortable(column)) {
    return toArraySafe(rows);
  }

  const indexedRows = toArraySafe(rows).map((row, originalIndex) => {
    const sortValue = getRowSortValue({
      row,
      column,
      columnKey: safeColumnKey,
    });

    const blankOrPadded =
      row?.[INTERNAL_ROW_FLAGS.IS_BLANK] === true ||
      row?.[INTERNAL_ROW_FLAGS.IS_PADDED] === true;

    return {
      row,
      originalIndex,
      sortValue,
      blankOrPadded,
    };
  });

  const sortableRows = [];
  const bottomRows = [];

  indexedRows.forEach((item) => {
    if (item.blankOrPadded || isEmptySortValue(item.sortValue)) {
      bottomRows.push(item);
      return;
    }

    sortableRows.push(item);
  });

  const sortedRows = [...sortableRows].sort((a, b) => {
    const valueComparison = compareSpreadsheetSortValues({
      a: a.sortValue,
      b: b.sortValue,
      direction: safeDirection,
    });

    if (valueComparison !== 0) {
      return valueComparison;
    }

    return a.originalIndex - b.originalIndex;
  });

  return [
    ...sortedRows,
    ...bottomRows,
  ].map((item) => item.row);
};

/* -------------------------------------------------------------------------- */
/* Cell addressing and movement                                               */
/* -------------------------------------------------------------------------- */

export const buildCellAddress = ({
  groupId,
  rowId,
  columnKey,
} = {}) => ({
  groupId: normalizeString(groupId, ''),
  rowId: normalizeString(rowId, ''),
  columnKey: normalizeString(columnKey, ''),
});

export const areCellAddressesEqual = (a, b) => (
  normalizeString(a?.groupId, '') === normalizeString(b?.groupId, '') &&
  normalizeString(a?.rowId, '') === normalizeString(b?.rowId, '') &&
  normalizeString(a?.columnKey, '') === normalizeString(b?.columnKey, '')
);

const buildNavigationCellKey = ({
  groupId,
  rowId,
  columnKey,
} = {}) => (
  `${normalizeString(groupId, '')}::${normalizeString(rowId, '')}::${normalizeString(columnKey, '')}`
);

const buildRowColumnLookupKey = ({
  rowIndex,
  groupId,
  columnKey,
} = {}) => (
  `${Number.isFinite(Number(rowIndex)) ? Number(rowIndex) : -1}::${normalizeString(groupId, '')}::${normalizeString(columnKey, '')}`
);

const buildGroupColumnLookupKey = ({
  groupId,
  columnKey,
} = {}) => (
  `${normalizeString(groupId, '')}::${normalizeString(columnKey, '')}`
);

const buildCellAddressFromNavigationCell = (cell = null) => {
  if (!cell) return null;

  return buildCellAddress({
    groupId: cell.groupId,
    rowId: cell.rowId,
    columnKey: cell.columnKey,
  });
};

const resolveDifferentCellAddress = ({
  currentCell,
  nextCell,
} = {}) => {
  const nextAddress = buildCellAddressFromNavigationCell(nextCell);

  if (!nextAddress) {
    return null;
  }

  if (areCellAddressesEqual(currentCell, nextAddress)) {
    return null;
  }

  return nextAddress;
};

/**
 * buildSpreadsheetNavigationIndex
 * -----------------------------------------------------------------------------
 * Builds a reusable navigation model for the unified grouped spreadsheet.
 *
 * WHY THIS EXISTS
 *  • resolveNextCellAddress(...) used to rebuild flat row cell sequences on
 *    every Arrow / Enter / Tab event.
 *  • Large Form Lists sheets can have many groups and visible columns, so
 *    doing that work on every keydown creates visible keyboard lag.
 *  • This index lets the canvas pay the derivation cost only when local sheet
 *    data changes, not on every navigation key.
 *
 * UNIFIED SHEET MODEL
 *  • Each visual row is a flat sequence across all groups:
 *      Group 1 Option → Group 1 Hint → Group 1 Dep →
 *      Group 2 Option → Group 2 Hint → Group 2 Dep → ...
 *  • Vertical movement keeps the same groupId + columnKey on the adjacent row.
 *
 * COMMAND / CONTROL EDGE NAVIGATION
 *  • Command/Ctrl + ArrowLeft  → first visible cell in the current visual row.
 *  • Command/Ctrl + ArrowRight → last visible cell in the current visual row.
 *  • Command/Ctrl + ArrowUp    → first visible cell in the same grouped column.
 *  • Command/Ctrl + ArrowDown  → last visible cell in the same grouped column.
 *
 * ENTERPRISE RULES
 *  • This is domain-free.
 *  • This does not know about Form Lists or SmartLists.
 *  • This does not read DOM.
 *  • This does not read/write storage, context, or Firestore.
 *  • It is safe for future row virtualization because cell identity remains
 *    groupId + rowId + columnKey.
 */
export const buildSpreadsheetNavigationIndex = ({
  groups = [],
  columns = [],
  rowKey = 'rows',
  rowIdKey = DEFAULT_ROW_ID_KEY,
} = {}) => {
  const safeGroups = toArraySafe(groups);
  const rowsByIndex = [];
  const cellPositionByKey = new Map();
  const cellByRowGroupColumnKey = new Map();
  const cellsByGroupColumnKey = new Map();

  const maxRowCount = safeGroups.reduce((max, group) => (
    Math.max(max, toArraySafe(group?.[rowKey]).length)
  ), 0);

  for (let rowIndex = 0; rowIndex < maxRowCount; rowIndex += 1) {
    const rowCells = [];

    safeGroups.forEach((group, groupIndex) => {
      const groupId = getGroupId(group);
      const rows = toArraySafe(group?.[rowKey]);
      const row = rows[rowIndex];

      if (!groupId || !row) return;

      const rowId = getRowId(row, rowIdKey);
      if (!rowId) return;

      const groupColumns =
        Array.isArray(group?.columns) && group.columns.length > 0
          ? group.columns
          : columns;

      getVisibleColumns(groupColumns).forEach((column, columnIndex) => {
        const columnKey = getColumnKey(column);
        if (!columnKey) return;

        const cell = {
          group,
          groupId,
          groupIndex,
          row,
          rowId,
          rowIndex,
          column,
          columnKey,
          columnIndex,
        };

        const flatCellIndex = rowCells.length;
        const groupColumnKey = buildGroupColumnLookupKey({
          groupId,
          columnKey,
        });

        rowCells.push(cell);

        cellPositionByKey.set(
          buildNavigationCellKey({
            groupId,
            rowId,
            columnKey,
          }),
          {
            rowIndex,
            flatCellIndex,
            cell,
          },
        );

        cellByRowGroupColumnKey.set(
          buildRowColumnLookupKey({
            rowIndex,
            groupId,
            columnKey,
          }),
          cell,
        );

        if (!cellsByGroupColumnKey.has(groupColumnKey)) {
          cellsByGroupColumnKey.set(groupColumnKey, []);
        }

        cellsByGroupColumnKey.get(groupColumnKey).push(cell);
      });
    });

    rowsByIndex[rowIndex] = rowCells;
  }

  return {
    rowCount: maxRowCount,
    rowsByIndex,
    cellPositionByKey,
    cellByRowGroupColumnKey,
    cellsByGroupColumnKey,
  };
};

const resolveEdgeCellAddress = ({
  currentCell = null,
  currentPosition = null,
  direction = 'right',
  navigationIndex = null,
} = {}) => {
  if (!currentCell || !currentPosition || !navigationIndex) {
    return null;
  }

  const {
    rowIndex,
  } = currentPosition;

  const rowCells = navigationIndex.rowsByIndex?.[rowIndex] || [];

  let nextCell = null;

  if (direction === 'right') {
    nextCell = rowCells[rowCells.length - 1] || null;
  }

  if (direction === 'left') {
    nextCell = rowCells[0] || null;
  }

  if (direction === 'down' || direction === 'up') {
    const groupColumnKey = buildGroupColumnLookupKey({
      groupId: currentCell.groupId,
      columnKey: currentCell.columnKey,
    });

    const columnTrack =
      navigationIndex.cellsByGroupColumnKey?.get(groupColumnKey) || [];

    nextCell =
      direction === 'down'
        ? columnTrack[columnTrack.length - 1] || null
        : columnTrack[0] || null;
  }

  return resolveDifferentCellAddress({
    currentCell,
    nextCell,
  });
};

export const resolveNextCellAddress = ({
  groups = [],
  columns = [],
  currentCell = null,
  direction = 'right',
  rowKey = 'rows',
  rowIdKey = DEFAULT_ROW_ID_KEY,
  navigationIndex = null,
  wrapRows = true,
  jumpToEdge = false,
} = {}) => {
  const safeCurrent = currentCell || {};

  /**
   * Backward-compatible fallback.
   *
   * IMPORTANT
   *  • Existing callers may still call resolveNextCellAddress(...) without a
   *    prebuilt index.
   *  • In that case, we build one locally and preserve behavior.
   *  • GroupedSpreadsheetCanvas passes a memoized index so keyboard navigation
   *    does not rebuild this structure on every keydown.
   */
  const index = navigationIndex || buildSpreadsheetNavigationIndex({
    groups,
    columns,
    rowKey,
    rowIdKey,
  });

  if (!index || index.rowCount <= 0) {
    return null;
  }

  const currentPosition = index.cellPositionByKey?.get(
    buildNavigationCellKey({
      groupId: safeCurrent.groupId,
      rowId: safeCurrent.rowId,
      columnKey: safeCurrent.columnKey,
    }),
  );

  if (!currentPosition) {
    return null;
  }

  if (jumpToEdge) {
    return resolveEdgeCellAddress({
      currentCell: safeCurrent,
      currentPosition,
      direction,
      navigationIndex: index,
    });
  }

  const {
    rowIndex,
    flatCellIndex,
  } = currentPosition;

  const rowCells = index.rowsByIndex?.[rowIndex] || [];

  let nextCell = null;

  if (direction === 'right') {
    nextCell = rowCells[flatCellIndex + 1] || null;

    if (!nextCell && wrapRows && rowIndex + 1 < index.rowCount) {
      const nextRowCells = index.rowsByIndex?.[rowIndex + 1] || [];
      nextCell = nextRowCells[0] || null;
    }
  }

  if (direction === 'left') {
    nextCell = rowCells[flatCellIndex - 1] || null;

    if (!nextCell && wrapRows && rowIndex - 1 >= 0) {
      const previousRowCells = index.rowsByIndex?.[rowIndex - 1] || [];
      nextCell = previousRowCells[previousRowCells.length - 1] || null;
    }
  }

  if (direction === 'down' || direction === 'up') {
    const nextRowIndex =
      direction === 'down' ? rowIndex + 1 : rowIndex - 1;

    if (nextRowIndex < 0 || nextRowIndex >= index.rowCount) {
      return null;
    }

    // Vertical movement keeps the same logical column identity:
    // same groupId + same columnKey on the next/previous visual row.
    nextCell = index.cellByRowGroupColumnKey?.get(
      buildRowColumnLookupKey({
        rowIndex: nextRowIndex,
        groupId: safeCurrent.groupId,
        columnKey: safeCurrent.columnKey,
      }),
    ) || null;
  }

  if (!nextCell) {
    return null;
  }

  return buildCellAddress({
    groupId: nextCell.groupId,
    rowId: nextCell.rowId,
    columnKey: nextCell.columnKey,
  });
};

/* -------------------------------------------------------------------------- */
/* Clipboard helpers                                                          */
/* -------------------------------------------------------------------------- */

export const parseClipboardTextToGrid = (text = '') => {
  const raw = typeof text === 'string' ? text : '';

  if (!raw) return [];

  return raw
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line, index, lines) => {
      const isLastEmptyLine =
        index === lines.length - 1 &&
        line.trim() === '';

      return !isLastEmptyLine;
    })
    .map((line) => line.split('\t'));
};

export const normalizePastedValue = ({
  value,
  column,
  row,
} = {}) => {
  if (typeof column?.normalizePastedValue === 'function') {
    return column.normalizePastedValue(value, row, column);
  }

  if (column?.type === 'number') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : '';
  }

  if (column?.type === 'select') {
    return normalizeString(value, '');
  }

  return typeof value === 'string' ? value : String(value ?? '');
};

/* -------------------------------------------------------------------------- */
/* Diagnostics                                                                */
/* -------------------------------------------------------------------------- */

export const buildSpreadsheetDiagnostics = ({
  groups = [],
  dirtyState = null,
  rowKey = 'rows',
} = {}) => {
  const safeGroups = toArraySafe(groups);

  return {
    groupCount: safeGroups.length,
    rowCount: safeGroups.reduce((sum, group) => (
      sum + toArraySafe(group?.[rowKey]).length
    ), 0),
    dirtyGroupIds: getDirtyGroupIdsArray(dirtyState),
  };
};